'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { type RootState } from '@/store/store';
import { useAppFilters } from '@/hooks';
import { useParams } from 'react-router-dom';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
// import { Top20AlarmsChartCard } from '../../../shared/Top20AlarmsChartCard'; // REMOVED - integrated into sensors
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import {
  useManagedGraphs,
  useCombinedLoadingState,
  useVerticalLines,
  useIntervalLines,
  useDialogState,
} from '@/hooks/shared/DataLayers';

import { createDataLayersConfig, DATA_LAYERS_RENDER_ORDER } from './config/data-layers-config';
import { IntervalDialog } from '@/modules/AssetsStudioPage/SensorFusion/components/charts-section/intervals';
import {
  createInitializationKey,
  extractGraphConfigurations,
  initializeEnabledGraphs,
} from './utils/initialize-graphs';
import {
  useEnhancedQueryParams,
  useSensorQueryParams,
} from '@/modules/AssetsStudioPage/SensorFusion/hooks/use-sensor-query-params';

interface SensorDataProps {
  asset: any;
  components: any[];
  diagnostics: any[];
  config: any;
  activeOperationalModes: string[];
  activeDataLayers: string[];
  onLoadingStateChange?: (isLoading: boolean) => void; // NEW: Loading state callback
  triggerRefresh?: number; // NEW: Trigger refresh when tab becomes active
  selectedMarkerTimestamp?: string | null; // NEW: Selected marker timestamp from Spectrum for highlighting
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void; // NEW: Setter for selected marker timestamp
  selectedTimestampsSet?: Set<string>; // Selected timestamps in focus mode
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void; // Set selected timestamps in focus mode
  sensorFusionVerticalLinesState?: ReturnType<typeof useVerticalLines>; // SensorFusion's own vertical lines state from parent
  spectrumVerticalLinesState?: ReturnType<typeof useVerticalLines>; // Spectrum's vertical lines state from parent
  benchmarkLineBySection?: Record<string, string | null>; // Shared benchmark state from parent
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>; // Setter for benchmark state
  isTabView: boolean;
}
//@TODO: CONSOLIDATE AND REMOVE CODE DUPLICATIONS!
export const GraphsContainer = ({
  asset,
  components,
  diagnostics,
  config,
  activeOperationalModes,
  activeDataLayers,
  onLoadingStateChange, // NEW: Callback to communicate loading state to parent
  triggerRefresh = 0,
  selectedMarkerTimestamp, // NEW: Selected marker timestamp from Spectrum for highlighting
  setSelectedMarkerTimestamp, // NEW: Setter for selected marker timestamp
  selectedTimestampsSet, // Selected timestamps in focus mode
  setSelectedTimestampsSet, // Set selected timestamps in focus mode
  sensorFusionVerticalLinesState, // SensorFusion's own vertical lines state from parent
  spectrumVerticalLinesState, // Spectrum's vertical lines state from parent
  benchmarkLineBySection, // Shared benchmark state from parent
  setBenchmarkLineBySection, // Setter for benchmark state
  isTabView,
}: SensorDataProps) => {
  const { isMobileAsset } = useActiveAssetsType();
  const [, setTriggerVisualAIReset] = useState(false);
  const [forceDataRefresh, setForceDataRefresh] = useState(0);

  // Global loading sensors state - synchronized across all legends in all sections
  const [loadingSensors, setLoadingSensors] = useState<Set<string>>(new Set());

  // Synced tool states for markers/harmonics/sideBands across all sections
  const [markerSelectionModeByChart, setMarkerSelectionModeByChart] = useState<Record<string, boolean>>({});
  const [harmonicSelectionModeByChart, setHarmonicSelectionModeByChart] = useState<Record<string, boolean>>({});
  const [sideBandsSelectionModeByChart, setSideBandsSelectionModeByChart] = useState<Record<string, boolean>>({});

  const { siteId, id: assetId } = useParams();
  const assetName = asset?.asset_name;
  const { selectedComponent: selectedComponentId } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const { selectedComponentObj } = useSelector((state: RootState) => state.assetsStudio);

  // Use combined loading state hook
  const { handleVisualAILoadingStateChange, loadingStates } = useCombinedLoadingState({
    onLoadingStateChange,
    selectedComponentId,
    selectedPart: null,
  });

  // Trigger data refresh when tab becomes active
  const prevTriggerRefreshRef = useRef(triggerRefresh);
  useEffect(() => {
    if (triggerRefresh !== prevTriggerRefreshRef.current && triggerRefresh > 0) {
      // Tab became active, forcing data refresh
      setForceDataRefresh((prev) => prev + 1);
      prevTriggerRefreshRef.current = triggerRefresh;
    }
  }, [triggerRefresh]);

  const selectedComponent = useMemo(
    () => components?.find((component) => component?.component_id === selectedComponentId),
    [components, selectedComponentId]
  );

  const baseTitle = selectedComponent?.component_ui_name || selectedComponent?.component_name;
  const stableOperationalModesRef = useRef<string[]>([]);
  useEffect(() => {
    const current = JSON.stringify(activeOperationalModes);
    const previous = JSON.stringify(stableOperationalModesRef.current);
    if (current !== previous) {
      stableOperationalModesRef.current = activeOperationalModes;
    }
  }, [activeOperationalModes]);
  // Initialize ref once
  const isFullPullRef = useRef(false);

  // Always update latest value in render phase
  if (Array.isArray(activeOperationalModes)) {
    isFullPullRef.current = activeOperationalModes.includes('full_pull');
  }

  const isFullPull = isFullPullRef.current;
  const queryParams = useSensorQueryParams({
    siteId,
    assetId,
    assetName,
    selectedComponentId,
    isMobileAsset,
    from,
    to,
    isFullPull,
  });

  // Extract graph configurations from config - memoized to prevent re-creation
  const { visualAIBeltGraphs, visualAIRockGraphs } = useMemo(
    () => extractGraphConfigurations(config),
    [JSON.stringify(config?.graphs?.map((g: any) => g.id))]
  );

  const initializationKeyRef = useRef<string>('');

  // State for managing legend items visibility across all graphs
  // Initialize from the actual graph legend states
  const [legendItemStates, setLegendItemStates] = useState<Record<string, boolean>>(() => {
    const initialStates: Record<string, boolean> = {};

    [...visualAIBeltGraphs, ...visualAIRockGraphs].forEach((graph) => {
      graph.legend?.forEach((item: any) => {
        const enabled = item.enabled !== false;
        initialStates[item.id] = enabled;
      });
    });
    return initialStates;
  });

  // Function to toggle legend item visibility
  const toggleLegendItem = useCallback((itemId: string, enabled: boolean) => {
    setLegendItemStates((prev) => {
      const newState = {
        ...prev,
        [itemId]: enabled,
      };
      return newState;
    });
  }, []);

  // Store reference to sensor toggle handlers from containers
  const sensorToggleHandlersRef = useRef<Record<string, (sectionId: string, sensorName: string) => void>>({});

  // Store sensor data from containers in state so it triggers re-renders
  const [registeredSensorData, setRegisteredSensorData] = useState<{
    enabledSensors: Record<string, Record<string, boolean>>;
    sensorColors: Record<string, Record<string, string>>;
  }>({
    enabledSensors: {},
    sensorColors: {},
  });

  // Focus mode state - Map of sectionId -> focused sensor name for that section
  const [focusedSensors, setFocusedSensors] = useState<Record<string, string>>({});
  const savedEnabledSensorsRef = useRef<Record<string, Record<string, boolean>> | null>(null);

  // Compute global mode: 'focus' if ANY section is in focus mode, otherwise 'multi'
  const globalLegendSyncMode: 'multi' | 'focus' = useMemo(() => {
    const hasAnyFocus = Object.keys(focusedSensors).length > 0;
    return hasAnyFocus ? 'focus' : 'multi';
  }, [focusedSensors]);

  const legendSync = {
    // Global mode computed from section-specific focuss
    mode: globalLegendSyncMode,
    // Toggle mode - switches between focus and multi mode
    toggleMode: () => {
      if (globalLegendSyncMode === 'focus') {
        // Exit focus mode - clear all focuss to return to multi mode
        setFocusedSensors({});
      } else {
        // Enter focus mode - focus the first enabled sensor in each section
        const newFocuss: Record<string, string> = {};

        // For each data layer (section), find the first enabled sensor
        Object.keys(registeredSensorData.enabledSensors).forEach((sectionId) => {
          const enabledSensors = registeredSensorData.enabledSensors[sectionId];
          const firstEnabledSensor = Object.keys(enabledSensors).find(
            (sensorName) => enabledSensors[sensorName] === true
          );

          if (firstEnabledSensor) {
            newFocuss[sectionId] = firstEnabledSensor;
          }
        });

        setFocusedSensors(newFocuss);
      }
    },
    getSavedState: () => savedEnabledSensorsRef.current,
    clearSavedState: () => {
      savedEnabledSensorsRef.current = null;
    },
  };

  const handleFocus = useCallback(
    (sensorName: string, sectionId?: string) => {
      // CRITICAL: If no sectionId provided, cannot do section-specific focus
      if (!sectionId) {
        return;
      }

      // If clicking the currently focused sensor in THIS section (EyeOff), exit focus for this section
      const currentFocusForSection = focusedSensors[sectionId];
      const isTogglingOff = currentFocusForSection === sensorName;

      if (isTogglingOff) {
        setFocusedSensors((prev) => {
          const newState = { ...prev };
          delete newState[sectionId];
          return newState;
        });

        // Section-specific focus removed - no global mode change needed
        return;
      }

      // Store focus per section - multiple sections can have focuss simultaneously
      setFocusedSensors((prev) => ({
        ...prev,
        [sectionId]: sensorName,
      }));
    },
    [focusedSensors]
  );

  // Function to register sensor toggle handlers from containers
  const registerSensorToggleHandler = useCallback(
    (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => {
      sensorToggleHandlersRef.current[dataLayer] = handler;
    },
    []
  );

  // Function to register sensor data from containers
  const registerSensorData = useCallback(
    (dataLayer: string, enabledSensors: Record<string, boolean>, sensorColors: Record<string, string>) => {
      setRegisteredSensorData((prev) => ({
        enabledSensors: {
          ...prev.enabledSensors,
          [dataLayer]: enabledSensors,
        },
        sensorColors: {
          ...prev.sensorColors,
          [dataLayer]: sensorColors,
        },
      }));
    },
    []
  );

  // Function to toggle sensor (called from search filters)
  const handleSensorToggleFromSearch = useCallback(
    (sectionId: string, sensorName: string) => {
      // The sectionId is the graph ID, we need to find which data layer it belongs to
      let dataLayer = SENSOR_FUSION_DATA_LAYERS.SENSORS; // Default to sensors

      if (visualAIBeltGraphs.some((g: any) => g.id === sectionId)) {
        dataLayer = SENSOR_FUSION_DATA_LAYERS.beltMisalignment;
      } else if (visualAIRockGraphs.some((g: any) => g.id === sectionId)) {
        dataLayer = SENSOR_FUSION_DATA_LAYERS.rockSize;
      }

      const handler = sensorToggleHandlersRef.current[dataLayer];
      if (handler) {
        // Pass the sectionId (graph ID) directly to the handler
        handler(sectionId, sensorName);
      }
    },
    [visualAIBeltGraphs, visualAIRockGraphs]
  );

  // Function to add sensor (called from search filters for disabled sensors)
  // This just calls the same toggle handler - the container will enable if disabled
  const handleSensorAdditionFromSearch = handleSensorToggleFromSearch;

  const initalEnabled = useMemo(() => {
    const result = initializeEnabledGraphs(visualAIBeltGraphs, visualAIRockGraphs);

    const initKey = createInitializationKey(selectedComponentId, null, visualAIBeltGraphs, visualAIRockGraphs);

    if (initKey !== initializationKeyRef.current) {
      initializationKeyRef.current = initKey;
    }

    return result;
  }, [selectedComponentId, visualAIBeltGraphs, visualAIRockGraphs]);

  // Use generalized hooks for managing graph sections
  const visualAIBeltManager = useManagedGraphs({
    graphs: visualAIBeltGraphs,
    initialEnabled: initalEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.beltMisalignment,
  });

  const visualAIRockManager = useManagedGraphs({
    graphs: visualAIRockGraphs,
    initialEnabled: initalEnabled,
    dataLayerKey: SENSOR_FUSION_DATA_LAYERS.rockSize,
  });

  // All state management is now handled by the useManagedGraphs hooks

  const enhancedQueryParams = useEnhancedQueryParams(queryParams);

  // Use vertical lines state from parent if provided, otherwise create local fallback
  const localVerticalLinesState = useVerticalLines();
  const verticalLinesState = sensorFusionVerticalLinesState || localVerticalLinesState;

  // Create highlighted version of verticalLines when marker(s) are selected in Spectrum or expanded legend
  const verticalLinesWithHighlight = React.useMemo(() => {
    // Check if we have any selected timestamps (either single or multiple)
    const hasSelectedTimestamps = selectedMarkerTimestamp || (selectedTimestampsSet && selectedTimestampsSet.size > 0);

    if (!hasSelectedTimestamps || !verticalLinesState?.verticalLines) {
      return verticalLinesState;
    }

    // Find the marker(s) matching the selected timestamp(s) and highlight with purple
    const highlightedLines = verticalLinesState.verticalLines.map((line: any) => {
      // Convert line timestamp to ISO string for comparison with Spectrum timestamps
      const lineTimestampISO =
        line.timestamp instanceof Date
          ? line.timestamp.toISOString()
          : typeof line.timestamp === 'number'
          ? new Date(line.timestamp).toISOString()
          : String(line.timestamp);

      // Check if this timestamp is in the selected set
      const matches = Boolean(selectedTimestampsSet && selectedTimestampsSet.has(lineTimestampISO));

      // If this line matches, apply purple highlight
      if (matches) {
        return {
          ...line,
          color: '#7071F3', // Primary purple color (matching PlotlyChart)
          originalColor: line.originalColor || line.color, // Store original color
        };
      }

      // Restore original color if it was previously highlighted
      if (line.originalColor) {
        return {
          ...line,
          color: line.originalColor,
          originalColor: undefined,
        };
      }

      return line;
    });

    return {
      ...verticalLinesState,
      verticalLines: highlightedLines,
    };
  }, [
    verticalLinesState?.verticalLines?.length,
    verticalLinesState.verticalLines, // Track the actual array reference
    selectedMarkerTimestamp,
    selectedTimestampsSet?.size, // Track Set size for changes
    Array.from(selectedTimestampsSet || []).join(','), // Track actual timestamp values
  ]);
  // Interval lines state
  const { intervalLines, addIntervalLines, removeIntervalLine, clearIntervalLines } = useIntervalLines();

  // Dialog state for intervals
  const { showIntervalDialog, setShowIntervalDialog, handleShowIntervalDialog } = useDialogState();

  // Refs for data layer configuration - must be at component top level (React Rules of Hooks)
  const groupFilterResetRef = useRef<(() => void) | null>(null);
  const initialAlarmLegendStatesRef = useRef<Record<string, boolean> | null>(null);
  const sectionPrimaryDataLayerId = config.dataLayers[0].id;
  // Create declarative data layers configuration - NO MEMOIZATION like main
  const dataLayersConfig = createDataLayersConfig({
    activeDataLayers,
    isMobileAsset,
    assetName,
    diagnostics,
    baseTitle,
    from,
    to,
    visualAIBeltGraphs,
    visualAIRockGraphs,
    visualAIBeltManager,
    visualAIRockManager,
    enhancedQueryParams,
    forceDataRefresh,
    handleVisualAILoadingStateChange,
    loadingStates,
    selectedComponentId,
    selectedComponentObj,
    verticalLinesState: verticalLinesWithHighlight, // Pass highlighted lines for SensorFusion charts
    rawVerticalLinesState: verticalLinesState, // Pass SensorFusion's raw lines for cross-tab access
    spectrumVerticalLinesState, // Pass Spectrum's separate vertical lines state
    benchmarkLineBySection, // Pass shared benchmark state from parent
    setBenchmarkLineBySection, // Pass setter for benchmark state
    config,
    legendItemStates,
    toggleLegendItem,
    onToggleSensor: handleSensorToggleFromSearch,
    onAddSensor: handleSensorAdditionFromSearch,
    registerSensorToggleHandler,
    registerSensorData,
    allEnabledSensors: registeredSensorData.enabledSensors,
    allSensorColors: registeredSensorData.sensorColors,
    isSensorLoading: () => false, // Simple loading function - always return false
    // Interval lines props
    intervalLines,
    onOpenIntervalDialog: handleShowIntervalDialog,
    onClearIntervalLines: clearIntervalLines,
    removeIntervalLine,
    // Display mode and spectrum props
    asset,
    components,
    activeOperationalModes,
    // Refs for data layer configuration (React Rules of Hooks compliance)
    groupFilterResetRef,
    initialAlarmLegendStatesRef,
    // Global loading sensors state
    loadingSensors,
    setLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => new Set(prev).add(sensorName));
    },
    clearLoadingSensor: (sensorName: string) => {
      setLoadingSensors((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sensorName);
        return newSet;
      });
    },
    // Synced tool selection states (for syncTools feature)
    markerSelectionModeByChart,
    setMarkerSelectionModeByChart,
    harmonicSelectionModeByChart,
    setHarmonicSelectionModeByChart,
    sideBandsSelectionModeByChart,
    setSideBandsSelectionModeByChart,
    // Focus mode props
    legendSync,
    focusedSensors,
    onFocus: handleFocus,
    // Selected marker timestamp for highlighting in SensorFusion charts
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    // Selected timestamps set for focus mode multi-select
    selectedTimestampsSet,
    setSelectedTimestampsSet,
    setTriggerVisualAIReset,
    isTabView,
    sectionPrimaryDataLayerId,
  });

  // Map data layers to components with CSS-based visibility control
  // Always render but use display:none for hidden layers to prevent data reloading
  const dataLayersToComponent = dataLayersConfig.reduce((acc, layer) => {
    acc[layer.id] = <div key={layer.id}>{layer.component()}</div>;
    return acc;
  }, {} as Record<string, React.ReactNode>);

  return (
    <>
      {/* <div className="flex flex-col gap-4 max-h-full h-full flex-1 [&>div]:h-full [&>div>div]:h-full "> */}
      <div className="flex flex-col gap-4 max-h-full h-full flex-1 ">
        {DATA_LAYERS_RENDER_ORDER.map((id) => {
          if (id !== sectionPrimaryDataLayerId) return null;
          return <React.Fragment key={id}>{dataLayersToComponent[id]}</React.Fragment>;
        })}
      </div>
      <IntervalDialog
        open={showIntervalDialog}
        onClose={() => setShowIntervalDialog(false)}
        intervalLines={intervalLines}
        onAddIntervalLines={addIntervalLines}
        onRemoveIntervalLine={removeIntervalLine}
      />
    </>
  );
};
